import { motion } from "motion/react"
import { usePurchase } from "../../../context/purchaseContext"
import { useAppState } from "../../../context/appStateContext"
import useStrings from "../../../localization/useStrings"
import svg from "../../../utils/svg"
import useHasPro from "../../../hooks/useHasPro"
import { useThreeStepStrings } from "../useThreeStepExpirationStrings"
import { ShieldOffIcon } from "../icons"
import RedLight from "../RedLight/RedLight"
import styles from "./StepOne.module.scss"

interface StepOneProps {
  onSkipToPlans: () => void
}

const StepOne = ({ onSkipToPlans }: StepOneProps) => {
  const strings = useStrings()
  const expirationStrings = useThreeStepStrings()
  const { state: purchaseState } = usePurchase()
  const { state: appState } = useAppState()
  const hasPro = useHasPro()
  const isUltimate = purchaseState.ultimateSelected

  const expiredFeatures = [
    {
      Icon: svg.featureIcons.Star,
      label: isUltimate
        ? strings.threeStepExpiration.allUltimateFeatures
        : strings.threeStepExpiration.allPremiumFeatures,
      color: "#F59E0B",
    },
    {
      Icon: svg.featureIcons.CloudVault,
      label: isUltimate
        ? strings.bubbleExpiration.cloudVaultUltimate
        : strings.bubbleExpiration.cloudVaultGB,
      color: "#3B82F6",
    },
    {
      Icon: svg.featureIcons.SpaceSaver,
      label: strings.threeStepExpiration.spaceSaver,
      color: "#8B5CF6",
    },
    {
      Icon: svg.featureIcons.Trash,
      label: strings.trashRecovery,
      color: "#EF4444",
    },
    ...(!hasPro
      ? [
          {
            Icon: svg.featureIcons.Adfree,
            label: strings.adFree,
            color: "#10B981",
          },
          {
            Icon: svg.featureIcons.Decoy,
            label: strings.decoy,
            color: "#F97316",
          },
        ]
      : []),
  ]

  return (
    <div className={styles.container} style={{ position: "relative" }}>
      <div className={styles.contentArea}>
        {/* Warning Icon */}
        <motion.div
          initial={{ scale: 0, rotate: -20 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{
            type: "spring",
            stiffness: 200,
            damping: 15,
            delay: 0.2,
          }}
          className={styles.iconWrapper}
        >
          <div className={styles.warningIcon}>
            <ShieldOffIcon className={styles.shieldIcon} />
          </div>
        </motion.div>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className={styles.titleWrapper}
        >
          <h1 className={styles.title}>{expirationStrings.title}</h1>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className={styles.subtitle}
        >
          {expirationStrings.subtitle}
        </motion.p>

        {/* Feature Grid */}
        <div className={styles.featureGrid}>
          {expiredFeatures.map((feature, idx) => {
            const isLast =
              idx === expiredFeatures.length - 1 &&
              expiredFeatures.length % 2 !== 0
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + idx * 0.08 }}
                className={`${styles.featureCard} ${isLast ? styles.lastCard : ""}`}
              >
                <div
                  className={styles.featureIconBg}
                  style={{ backgroundColor: `${feature.color}15` }}
                >
                  <feature.Icon
                    className={styles.featureIcon}
                    style={{ color: feature.color }}
                  />
                </div>
                <span className={styles.featureLabel}>{feature.label}</span>
                <div className={styles.disabledRow}>
                  <RedLight size={7} color="#3B82F6" />
                  <span className={styles.disabledText}>
                    {strings.threeStepExpiration.disabled}
                  </span>
                </div>
              </motion.div>
            )
          })}
        </div>

        {/* Warning message */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1 }}
          className={styles.warningBanner}
        >
          <p className={styles.warningText}>
            {strings.threeStepExpiration.photosSafeWarning}
          </p>
        </motion.div>
      </div>

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2 }}
        className={styles.ctaWrapper}
      >
        <button
          onClick={onSkipToPlans}
          className={styles.ctaButton}
          style={
            isUltimate
              ? {
                  background:
                    "linear-gradient(to right, var(--ultimate-gradient-start), var(--ultimate-gradient-end))",
                }
              : undefined
          }
        >
          {strings.threeStepExpiration.continue}
        </button>
      </motion.div>

      <div
        className={styles.bottomInset}
        style={{ height: appState.initialData?.originalBottomInset ?? 0 }}
      />
    </div>
  )
}

export default StepOne
